import React, { useState, useEffect, useRef } from 'react';

const IBChatbot = () => {
  // State variables
  const [question, setQuestion] = useState('');
  const [dataAnswer, setDataAnswer] = useState({ acts: '', rules: '' });
  const [caseLaws, setCaseLaws] = useState({
    supreme_court: [],
    high_court: [],
    nclat: [],
    nclt: []
  });
  const [selectedCase, setSelectedCase] = useState(null);
  const [clipboardContent, setClipboardContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Use environment variable for production API, fallback to localhost for local dev
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const [activeTab, setActiveTab] = useState('acts');
  const [activeCaseTab, setActiveCaseTab] = useState('supreme_court');
  const [apiStatus, setApiStatus] = useState('connecting');
  const [completedSearches, setCompletedSearches] = useState({
    acts: false,
    rules: false,
    supreme_court: false,
    high_court: false,
    nclat: false,
    nclt: false
  });
  
  // Refs
  const answerBoxRef = useRef(null);
  const caseDetailsRef = useRef(null);
  const caseListRef = useRef(null);

  // API Configuration


  // Check backend connection on component mount
  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/collections`);
        if (response.ok) {
          setApiStatus('connected');
        } else {
          setApiStatus('error');
        }
      } catch (error) {
        console.error('Backend connection error:', error);
        setApiStatus('error');
      }
    };
    checkApiConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generic fetch function for a single collection
  const fetchCollectionData = async (collection, displayName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, collection, k: 3 })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${displayName} data`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${displayName}:`, error);
      // Return a specific error structure to be handled later
      if (collection === 'acts' || collection === 'rules') {
        return { answer: `Error loading ${displayName} data` };
      }
      return { sources: [] }; // For case laws, return empty array on error
    }
  };

  // Handle form submission with new sequential logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || apiStatus !== 'connected') return;

    // 1. Reset all states and start loading
    setIsLoading(true);
    setDataAnswer({ acts: '', rules: '' });
    setCaseLaws({ supreme_court: [], high_court: [], nclat: [], nclt: [] });
    setSelectedCase(null);
    setCompletedSearches({ acts: false, rules: false, supreme_court: false, high_court: false, nclat: false, nclt: false });

    // 2. Fetch Acts and Rules in parallel and wait for both to complete
    try {
      const [actsResult, rulesResult] = await Promise.all([
        fetchCollectionData('acts', 'Acts'),
        fetchCollectionData('rules', 'Rules')
      ]);
      
      // This state update happens only AFTER both are fetched
      setDataAnswer({
        acts: actsResult.answer,
        rules: rulesResult.answer,
      });

    } catch(error) {
       console.error("Error fetching Acts & Rules:", error);
       setDataAnswer({
         acts: 'An unexpected error occurred while fetching Acts.',
         rules: 'An unexpected error occurred while fetching Rules.',
       });
    } finally {
        setCompletedSearches(prev => ({ ...prev, acts: true, rules: true }));
    }


    // 3. Fetch Case Laws sequentially
    const caseCollections = [
      { name: 'supreme_court', displayName: 'Supreme Court' },
      { name: 'high_court', displayName: 'High Court' },
      { name: 'nclat', displayName: 'NCLAT' },
      { name: 'nclt', displayName: 'NCLT' }
    ];

    for (const collection of caseCollections) {
      const caseResult = await fetchCollectionData(collection.name, collection.displayName);
      
      // Update state for each court as it completes
      setCaseLaws(prev => ({
        ...prev,
        [collection.name]: caseResult.sources || []
      }));
      setCompletedSearches(prev => ({ ...prev, [collection.name]: true }));
    }

    // 4. All searches are done, stop loading
    setIsLoading(false);
  };

  // Case selection handler
  const handleCaseClick = (caseLaw) => {
    setSelectedCase(caseLaw);
  };

  // Add content to clipboard
  const handleAddToClipboard = (content) => {
    if (content) {
      setClipboardContent(prev => prev + (prev ? '\n\n' : '') + content);
    }
  };

  // Reset all state
  const handleRefresh = () => {
    if (clipboardContent) downloadClipboard();
    setQuestion('');
    setDataAnswer({ acts: '', rules: '' });
    setCaseLaws({ supreme_court: [], high_court: [], nclat: [], nclt: [] });
    setSelectedCase(null);
    setClipboardContent('');
    setCompletedSearches({ acts: false, rules: false, supreme_court: false, high_court: false, nclat: false, nclt: false });
  };

  // Copy text to system clipboard
  const copyToClipboard = (text) => {
     try {
      navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    } catch (err) {
      console.error('Copy failed:', err);
      // Fallback for older browsers or insecure contexts
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position="fixed"; //avoid scrolling to bottom
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Copied to clipboard!');
      } catch (err) {
        alert('Failed to copy text.');
      }
      document.body.removeChild(textArea);
    }
  };

  // Download clipboard content
  const downloadClipboard = () => {
    const blob = new Blob([clipboardContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IBChatbot-Notes-${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Auto-scroll behaviors
  useEffect(() => {
    if (answerBoxRef.current) {
      answerBoxRef.current.scrollTop = answerBoxRef.current.scrollHeight;
    }
  }, [dataAnswer]);

  useEffect(() => {
    if (caseDetailsRef.current && selectedCase) {
      caseDetailsRef.current.scrollTop = 0;
    }
  }, [selectedCase]);

  const activeCourtCases = caseLaws[activeCaseTab] || [];

  // Status indicators for each search
  const getSearchStatus = (collection) => {
    if (isLoading && !completedSearches[collection]) return 'Searching...';
    if (!completedSearches[collection]) return 'Pending';
    
    if (collection === 'acts' || collection === 'rules') {
       return dataAnswer[collection] && !dataAnswer[collection].startsWith('Error') ? 'Found' : 'Not found';
    }
    return caseLaws[collection]?.length > 0 ? `Found (${caseLaws[collection].length})` : 'Not found';
  };

  const css = `
    :root {
      --primary-bg: #f3f4f6;
      --secondary-bg: #ffffff;
      --border-color: #e5e7eb;
      --text-primary: #111827;
      --text-secondary: #6b7280;
      --accent-color: #1769E8;
      --accent-hover: #1258c7;
      --status-connected: #10b981;
      --status-error: #ef4444;
      --status-pending: #f59e0b;
      --box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
      --box-shadow-md: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06);
    }

    * {
      box-sizing: border-box;
    }

    .ibc-chatbot-container {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      display: flex;
      flex-direction: column;
      height: 100vh;
      width: 100%;
      background-color: var(--primary-bg);
      color: var(--text-primary);
      padding: 0;
      margin: 0;
      overflow: hidden;
    }

    .connection-status {
      position: fixed;
      top: 80px;
      left: 16px;
      padding: 4px 10px;
      font-size: 11px;
      font-weight: 600;
      border-radius: 6px;
      z-index: 100;
      letter-spacing: 0.3px;
    }
    .connection-status.connected { background-color: #d1fae5; color: #065f46; }
    .connection-status.error { background-color: #fee2e2; color: #991b1b; }
    .connection-status.connecting { background-color: #fef3c7; color: #92400e; }
    
    /* Page Header */
    .ibc-chatbot-container .top-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 24px;
      background: var(--secondary-bg);
      border-bottom: 1px solid var(--border-color);
      min-height: 52px;
      flex-shrink: 0;
    }
    .ibc-chatbot-container .logo { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      font-weight: 700 !important;
      font-size: 20px !important;
      color: #071B3A !important;
      letter-spacing: -0.5px;
    }
    .ibc-chatbot-container .title { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      font-size: 16px !important;
      font-weight: 600 !important;
      margin: 0 !important;
      color: #374151 !important;
      letter-spacing: -0.3px;
    }
    .refresh-button {
      padding: 6px 12px;
      font-size: 11px;
      font-weight: 600;
      background-color: var(--accent-color);
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;
    }
    .refresh-button:hover {
      background-color: var(--accent-hover);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(23, 105, 232, 0.3);
    }
    
    /* Status Filter Bar */
    .search-status-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      background-color: var(--secondary-bg);
      padding: 8px 12px;
      border-radius: 10px;
      margin: 8px 24px;
      font-size: 12px;
      border: 1px solid var(--border-color);
      box-shadow: var(--box-shadow);
    }
    .status-item {
      padding: 4px 10px;
      border-radius: 20px;
      background-color: #f3f4f6;
      color: var(--text-secondary);
      font-weight: 500;
      transition: all 0.2s ease;
      white-space: nowrap;
      font-size: 11px;
    }
    .status-item.completed { 
      background-color: #d1fae5; 
      color: #065f46; 
      font-weight: 600;
    }
    
    /* Main Content Grid */
    .main-content {
      display: grid;
      grid-template-columns: 32% 32% 36%;
      gap: 16px;
      flex-grow: 1;
      overflow: hidden;
      padding: 0 24px;
      min-height: 0;
      height: 0;
      align-content: stretch;
    }
    
    /* Content Panels */
    .content-box {
      background-color: var(--secondary-bg);
      border: 1px solid var(--border-color);
      border-radius: 12px;
      box-shadow: var(--box-shadow);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0;
      max-height: 100%;
    }

    .answer-box { grid-column: 1 / 2; }
    .case-law-box { grid-column: 2 / 3; }
    .case-details-box { grid-column: 3 / 4; }
    
    /* Panel Headers */
    .ibc-chatbot-container .box-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      border-bottom: 1px solid var(--border-color);
      flex-shrink: 0;
      min-height: 40px;
      gap: 8px;
    }
    .ibc-chatbot-container .box-header h2 { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      font-size: 13px !important;
      font-weight: 700 !important;
      margin: 0 !important;
      color: #1f2937 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.5px !important;
      white-space: nowrap !important;
      flex-shrink: 0 !important;
    }

    /* Tabs */
    .data-tabs, .case-tabs {
      display: flex;
      gap: 4px;
      flex-shrink: 1;
      min-width: 0;
      overflow-x: auto;
      scrollbar-width: none;
    }
    .data-tabs::-webkit-scrollbar, .case-tabs::-webkit-scrollbar {
      display: none;
    }

    .ibc-chatbot-container .tab-button {
      padding: 4px 10px;
      border: 1px solid var(--border-color);
      background-color: transparent;
      border-radius: 6px;
      cursor: pointer;
      font-size: 11px;
      font-weight: 500;
      transition: all 0.2s ease;
      white-space: nowrap;
      color: var(--text-secondary);
      flex-shrink: 0;
    }
    .ibc-chatbot-container .tab-button.active { 
      background-color: var(--accent-color) !important; 
      color: white !important; 
      border-color: var(--accent-color) !important; 
    }
    .ibc-chatbot-container .tab-button:not(.active):hover { 
      background-color: #f3f4f6; 
      border-color: #d1d5db;
    }
    
    /* Scrollable Content */
    .scrollable-content {
      padding: 12px;
      overflow-y: auto;
      flex-grow: 1;
      min-height: 200px;
    }
    .answer-content { 
      white-space: pre-wrap; 
      word-wrap: break-word;
      font-size: 13px;
      line-height: 1.6;
      color: #374151;
      min-height: 100px;
    }
    
    /* Case List */
    .case-list { 
      display: flex; 
      flex-direction: column; 
      gap: 8px; 
      min-height: 100px;
    }
    .case-item {
      padding: 10px 12px;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      background: var(--secondary-bg);
    }
    .case-item:hover { 
      border-color: var(--accent-color); 
      box-shadow: var(--box-shadow-md);
    }
    .case-item.active { 
      border-color: var(--accent-color); 
      background-color: #eff6ff; 
    }
    .case-item h3 { 
      font-size: 13px; 
      font-weight: 600;
      margin: 0 0 4px; 
      color: #1f2937;
      line-height: 1.4;
    }
    .case-item p { 
      font-size: 12px; 
      color: var(--text-secondary); 
      margin: 0; 
    }
    
    /* Case Details */
    .case-details { 
      min-height: 100px;
    }
    .case-details h3 { 
      margin-top: 0; 
      font-size: 15px;
      font-weight: 700;
      color: #1f2937;
    }
    .case-details h4 {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
      margin: 12px 0 6px;
    }
    .case-details p {
      font-size: 13px;
      line-height: 1.6;
      color: #4b5563;
    }
    
    /* Notes Section */
    .ibc-chatbot-container .clipboard-box {
      margin: 8px 24px;
      display: flex;
      flex-direction: column;
      background: var(--secondary-bg);
      border-radius: 10px;
      box-shadow: var(--box-shadow);
      border: 1px solid var(--border-color);
      max-height: 140px;
      flex-shrink: 0;
    }
    .ibc-chatbot-container .clipboard-box .box-header h2 {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      font-size: 13px !important;
      font-weight: 700 !important;
    }
    .clipboard-actions { 
      display: flex; 
      gap: 6px; 
    }
    .clipboard-actions button {
      padding: 4px 10px;
      font-size: 11px;
      font-weight: 500;
      border-radius: 6px;
    }
    .clipboard-content {
      flex-grow: 1;
      width: 100%;
      padding: 8px 10px;
      border: none;
      border-top: 1px solid var(--border-color);
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
      resize: none;
      font-family: inherit;
      font-size: 12px;
      line-height: 1.4;
      min-height: 60px;
    }
    .clipboard-content:focus { outline: none; }
    .clipboard-content::placeholder {
      color: #9ca3af;
    }
    
    /* Search Input */
    .chat-input {
      display: flex;
      margin: 0 24px 10px;
      gap: 8px;
      flex-shrink: 0;
    }
    .chat-input input {
      flex-grow: 1;
      padding: 8px 14px;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      font-size: 13px;
      background: var(--secondary-bg);
      transition: border-color 0.2s ease;
    }
    .chat-input input:focus { 
      outline: none; 
      border-color: var(--accent-color);
      box-shadow: 0 0 0 3px rgba(23, 105, 232, 0.1);
    }
    .chat-input input::placeholder {
      color: #9ca3af;
    }
    
    /* Buttons */
    button {
      padding: 6px 12px;
      font-size: 12px;
      font-weight: 600;
      border: none;
      border-radius: 6px;
      background-color: var(--accent-color);
      color: white;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    button:hover { 
      background-color: var(--accent-hover); 
    }
    button:disabled { 
      background-color: #d1d5db; 
      cursor: not-allowed; 
    }
    .copy-button { 
      background-color: #6b7280; 
    }
    .copy-button:hover { 
      background-color: #4b5563; 
    }
    
    /* Empty States */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 32px 16px;
      color: var(--text-secondary);
      font-size: 13px;
      min-height: 150px;
    }
    .empty-state-icon {
      font-size: 32px;
      margin-bottom: 12px;
      opacity: 0.5;
    }
    .empty-state-title {
      font-size: 14px;
      font-weight: 600;
      color: #4b5563;
      margin-bottom: 4px;
    }
    .empty-state-desc {
      font-size: 12px;
      color: #9ca3af;
    }
    
    .loading-spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      color: var(--text-secondary);
      font-size: 13px;
    }
    
    /* Responsive */
    @media (max-width: 1024px) {
      .main-content {
        grid-template-columns: 1fr 1fr;
        padding: 0 16px;
      }
      .answer-box { grid-column: 1 / 2; }
      .case-law-box { grid-column: 2 / 3; }
      .case-details-box { grid-column: 1 / 3; }
      .search-status-bar { margin: 12px 16px; }
      .clipboard-box { margin: 12px 16px; }
      .chat-input { margin: 0 16px 12px; }
    }

    @media (max-width: 640px) {
      .main-content {
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 0 12px;
      }
      .answer-box, .case-law-box, .case-details-box { grid-column: auto; }
      .top-bar { 
        flex-direction: row; 
        gap: 12px; 
        padding: 10px 16px;
        flex-wrap: wrap;
      }
      .logo { font-size: 22px; }
      .title { font-size: 16px; }
      .search-status-bar { 
        margin: 8px 12px; 
        padding: 8px 12px;
        gap: 6px;
      }
      .status-item {
        padding: 4px 10px;
        font-size: 11px;
      }
      .clipboard-box { 
        max-height: 150px; 
        margin: 8px 12px;
      }
      .chat-input { margin: 0 12px 10px; }
      .case-tabs {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
      }
      .case-tabs::-webkit-scrollbar {
        display: none;
      }
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="ibc-chatbot-container">
        {/* Connection status indicator */}
        <div className={`connection-status ${apiStatus}`}>
          Backend: {apiStatus}
        </div>

        {/* Top Bar */}
        <header className="top-bar">
          <div className="logo">IBC</div>
          <h1 className="title">Legal Research Assistant</h1>
          <button className="refresh-button" onClick={handleRefresh}>
            ↻ Refresh & Save
          </button>
        </header>

        {/* Search Status Bar */}
        <div className="search-status-bar">
          <div className={`status-item ${completedSearches.acts ? 'completed' : ''}`}>
            Acts: {getSearchStatus('acts')}
          </div>
          <div className={`status-item ${completedSearches.rules ? 'completed' : ''}`}>
            Rules: {getSearchStatus('rules')}
          </div>
          <div className={`status-item ${completedSearches.supreme_court ? 'completed' : ''}`}>
            SC: {getSearchStatus('supreme_court')}
          </div>
          <div className={`status-item ${completedSearches.high_court ? 'completed' : ''}`}>
            HC: {getSearchStatus('high_court')}
          </div>
          <div className={`status-item ${completedSearches.nclat ? 'completed' : ''}`}>
            NCLAT: {getSearchStatus('nclat')}
          </div>
          <div className={`status-item ${completedSearches.nclt ? 'completed' : ''}`}>
            NCLT: {getSearchStatus('nclt')}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="main-content">
          {/* Answer Box */}
          <div className="content-box answer-box">
            <div className="box-header">
              <h2> Acts & Rules </h2>
              <div className="data-tabs">
                <button 
                  className={`tab-button ${activeTab === 'acts' ? 'active' : ''}`}
                  onClick={() => setActiveTab('acts')}
                >
                  Acts
                </button>
                <button 
                  className={`tab-button ${activeTab === 'rules' ? 'active' : ''}`}
                  onClick={() => setActiveTab('rules')}
                >
                  Rules
                </button>
              </div>
            </div>
            <div className="scrollable-content" ref={answerBoxRef}>
              {isLoading && !completedSearches.rules ? (
                <div className="loading-spinner">
                  <span>Searching Acts & Rules...</span>
                </div>
              ) : (
                <div className="answer-content">
                  {dataAnswer[activeTab] ? (
                    dataAnswer[activeTab]
                  ) : (
                    <div className="empty-state">
                      <div className="empty-state-icon">📜</div>
                      <div className="empty-state-title">No data found</div>
                      <div className="empty-state-desc">
                        {completedSearches[activeTab] 
                          ? 'Try a different search query.' 
                          : 'Enter a query to search for acts and rules.'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Case Law List */}
          <div className="content-box case-law-box">
            <div className="box-header">
              <h2> Cases </h2>
              <div className="case-tabs">
                {[
                  { key: 'supreme_court', label: 'Supreme Court' },
                  { key: 'high_court', label: 'High Court' },
                  { key: 'nclat', label: 'NCLAT' },
                  { key: 'nclt', label: 'NCLT' }
                ].map(court => (
                  <button
                    key={court.key}
                    className={`tab-button ${activeCaseTab === court.key ? 'active' : ''}`}
                    onClick={() => setActiveCaseTab(court.key)}
                  >
                    {court.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="scrollable-content" ref={caseListRef}>
              <div className="case-list">
                {isLoading && !completedSearches[activeCaseTab] ? (
                  <div className="loading-spinner">
                    <span>Searching {activeCaseTab.replace('_', ' ')}...</span>
                  </div>
                ) : activeCourtCases.length > 0 ? (
                  activeCourtCases.map((caseLaw, index) => (
                    <div 
                      key={caseLaw.id || index} 
                      className={`case-item ${selectedCase === caseLaw ? 'active' : ''}`}
                      onClick={() => handleCaseClick(caseLaw)}
                    >
                      <h3>{caseLaw.metadata.case_name || `Case ${index + 1}`}</h3>
                      <p className="court-name">{caseLaw.metadata.court || 'Court not specified'}</p>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">⚖️</div>
                    <div className="empty-state-title">
                      {completedSearches[activeCaseTab] ? 'No cases found' : 'Search not started'}
                    </div>
                    <div className="empty-state-desc">
                      {completedSearches[activeCaseTab] 
                        ? 'Try a different search query.' 
                        : 'Enter a query to search for cases.'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Case Details */}
          <div className="content-box case-details-box">
            <div className="box-header">
              <h2>Case Details</h2>
              <button 
                className="copy-button" 
                onClick={() => handleAddToClipboard(
                    `Case: ${selectedCase.metadata.case_name || 'N/A'}\n` +
                    `Court: ${selectedCase.metadata.court || 'N/A'}\n` +
                    `Citation: ${selectedCase.metadata.citation || 'N/A'}\n` +
                    `Summary: ${selectedCase.page_content}`
                )}
                disabled={!selectedCase}
              >
                Add to Notes
              </button>
            </div>
            <div className="scrollable-content" ref={caseDetailsRef}>
              {selectedCase ? (
                <div className="case-details">
                  <h3>{selectedCase.metadata.case_name || 'Case Details'}</h3>
                  <p><strong>Court:</strong> {selectedCase.metadata.court || 'N/A'}</p>
                  <p><strong>Citation:</strong> {selectedCase.metadata.citation || 'N/A'}</p>
                  <h4>Summary:</h4>
                  <p>{selectedCase.page_content}</p>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">📋</div>
                  <div className="empty-state-title">Select a case</div>
                  <div className="empty-state-desc">Click on a case to view its details.</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clipboard Box */}
        <div className="clipboard-box">
          <div className="box-header">
            <h2>Notes</h2>
            <div className="clipboard-actions">
              <button onClick={() => copyToClipboard(clipboardContent)} disabled={!clipboardContent}>
                Copy
              </button>
              <button onClick={downloadClipboard} disabled={!clipboardContent}>
                Download
              </button>
              <button onClick={() => setClipboardContent('')} disabled={!clipboardContent}>
                Clear
              </button>
            </div>
          </div>
          <textarea
            className="clipboard-content"
            value={clipboardContent}
            onChange={(e) => setClipboardContent(e.target.value)}
            placeholder="Your notes and saved content will appear here..."
          />
        </div>

        {/* Question Input */}
        <form className="chat-input" onSubmit={handleSubmit}>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Write your questions here..."
            disabled={isLoading || apiStatus !== 'connected'}
          />
          <button 
            type="submit" 
            disabled={isLoading || apiStatus !== 'connected'}
          >
            {isLoading ? 'Searching...' : 'Ask'}
          </button>
        </form>
      </div>
    </>
  );
};

export default IBChatbot;
